<template>
  <section
    class="my-8 md:mb-8 md:mt-8 mx-auto bg-black pt-6 px-2 md:px-10 pb-0 shadow-black shadow-xl divide-y-2 divide-white max-w-[1400px]"
  >
    <RallyLogoDownload :to="selfPath" />
    <LanguageNav v-if="isTranslatedFamily" :current="lang" :options="languageOptions" />
    <div class="mt-8 py-4">
      <h1 class="text-white text-2xl md:text-4xl font-russo mt-6">
        {{ c.h1 }}
      </h1>
      <PhoneDownloadNotice
        :message="c.phone.message"
        :copy-label="c.phone.copy"
        :copied-label="c.phone.copied"
        :url="`https://www.rallynavigator.com${selfPath}`"
      />
      <ul
        class="font-helvetica text-[16px] md:text-[22px] font-bold text-white text-xl flex flex-col gap-6 mt-4 mb-6"
      >
        <li
          class="flex bg-[#262626] p-6 border-4 border-[#FF0000] rounded-full hover:underline mx-auto"
        >
          <a
            class="text-center m-auto"
            href="https://downloads.rallynavigator.com/Rally%20Navigator%20Installer.exe"
            @click="trackDownload('windows')"
            >{{ c.btnWindows }}</a
          >
        </li>
        <li
          class="flex bg-[#262626] p-6 border-4 border-[#FF0000] rounded-full hover:underline mx-auto"
        >
          <a
            class="text-center m-auto"
            href="https://downloads.rallynavigator.com/Rally%20Navigator%20Installer.dmg"
            @click="trackDownload('mac')"
            >{{ c.btnMac }}</a
          >
        </li>
        <li
          class="flex bg-[#262626] p-6 border-4 border-[#FF0000] rounded-full hover:underline mx-auto"
        >
          <a
            class="text-center m-auto"
            href="https://downloads.rallynavigator.com/Rally%20Navigator%20Installer.AppImage"
            @click="trackDownload('linux')"
            >{{ c.btnLinux }}</a
          >
        </li>
      </ul>

      <!-- Latest published version + release date, read from the download
           server at build time. Not hand-maintained. -->
      <p
        class="font-helvetica text-[14px] md:text-[16px] text-[#999999] text-center"
      >
        {{ c.latestVersion }}
        <span class="text-white font-bold">{{ latestVersion }}</span>
        &middot; {{ c.released }} {{ localDate(releaseDate) }}
        &middot;
        <router-link to="/whats-new" class="text-[#FF0000] hover:underline"
          >{{ c.whatsNew }} &rarr;</router-link
        >
      </p>
      <!-- Trust / safety signals (verified true for the published build) -->
      <p
        class="font-helvetica text-[13px] md:text-[15px] text-[#999999] mt-3 flex flex-wrap justify-center items-center gap-x-4 gap-y-1"
      >
        <span v-for="(t, i) in c.trust" :key="i" v-html="'&#10003; ' + t"></span>
      </p>

      <!-- The PREVIOUS release, one row, so a buggy new release never leaves anyone without a fallback
           (owner, 2026-09-26). Read at build time from the download server's previous.json, which the
           desktop repo's release-upload.sh rewrites on every release: the release that was live
           becomes this row. Hidden when there is none, or if it would name the current version. -->
      <div
        v-if="previous.version && previous.version !== latestVersion"
        class="mt-8 mx-auto max-w-[720px] border-t border-[#333333] pt-6"
      >
        <p
          class="font-helvetica text-[15px] md:text-[18px] font-bold text-white text-center"
        >
          {{ c.previousTitle }}
        </p>
        <p
          class="font-helvetica text-[13px] md:text-[14px] text-[#999999] text-center mt-1"
        >
          {{ c.versionWord }} <span class="text-white">{{ previous.version }}</span>
          <template v-if="previous.releaseDate">
            &middot; {{ c.released }} {{ localDate(previous.releaseDate) }}</template
          >
        </p>
        <ul
          class="grid grid-cols-3 gap-2 md:gap-4 mt-4 font-helvetica text-[13px] md:text-[16px] font-bold text-white"
        >
          <li v-for="p in previousPlatforms" :key="p.key">
            <a
              v-if="p.url"
              class="block text-center bg-[#262626] border-2 border-[#666666] rounded-full py-2 md:py-3 px-2 hover:underline hover:border-white"
              :href="p.url"
              @click="trackDownload(`${p.key}_previous`, p.url)"
              >{{ p.label }}</a
            >
          </li>
        </ul>
      </div>
    </div>
    <div class="py-8 flex flex-col gap-8">
      <h2 class="text-[#FF0000] text-2xl md:text-3xl font-russo">
        {{ c.howH2 }}
      </h2>
      <div
        class="grid md:grid-cols-2 gap-8 font-helvetica text-[16px] md:text-[20px] font-bold text-[#999999]"
      >
        <div class="flex flex-col gap-4">
          <h3 class="text-white text-[20px] md:text-[24px]">
            {{ c.newH3 }}
          </h3>
          <ol class="flex flex-col gap-3">
            <li v-for="(step, i) in c.newSteps" :key="i" v-html="step"></li>
          </ol>
        </div>
        <div class="flex flex-col gap-4">
          <h3 class="text-white text-[20px] md:text-[24px]">
            {{ c.existingH3 }}
          </h3>
          <ul class="flex flex-col gap-3">
            <li v-for="(item, i) in c.existing" :key="i" v-html="item"></li>
          </ul>
        </div>
      </div>
    </div>
    <hr class="border-t-2 border-white" />
    <div class="py-8 flex flex-col gap-6">
      <h2 class="text-white text-2xl md:text-3xl font-russo" v-html="c.plansH2"></h2>
      <p
        class="font-helvetica text-[15px] md:text-[18px] font-bold text-[#999999]"
        v-html="c.plansP"
      ></p>
      <PricingTable :t="c.pricing" />
    </div>
    <div class="py-8 flex flex-col gap-6">
      <h2 class="text-white font-russo text-[22px] md:text-[28px]">
        {{ c.trialH2 }}
      </h2>
      <div
        class="font-helvetica text-[15px] md:text-[18px] font-bold text-[#999999] flex flex-col gap-4"
      >
        <p v-for="(para, i) in c.trialPs" :key="i" v-html="para"></p>
      </div>
    </div>
    <div class="py-8">
      <h2 class="text-white font-russo text-[22px] md:text-[28px]">
        {{ c.newFeaturesH2 }}
      </h2>
      <ul
        class="font-helvetica text-[16px] md:text-[22px] font-semibold text-[#999999] mt-4 items-center flex flex-col gap-2 md:gap-4"
      >
        <li v-for="(f, i) in c.newFeatures" :key="i"><p v-html="f"></p></li>
      </ul>
    </div>
    <div class="py-8 flex flex-col gap-8">
      <h2 class="text-white font-russo text-[22px] md:text-[28px]">
        {{ c.feedbackH2 }}
      </h2>
      <h2
        class="text-[#FF0000] hover:underline font-helvetica text-[18px] md:text-[22px] font-black"
      >
        <a
          href="mailto:support@rallynavigator.com?subject=Feedback%20from%20Rally%20Navigator%202.0%20Beta%20Test"
        >
          SUPPORT@RALLYNAVIGATOR.COM
        </a>
      </h2>
      <picture class="contents">
        <source srcset="/images/download/toolbar3-1.webp" type="image/webp" />
        <img
          decoding="async"
          fetchpriority="high"
          width="1920"
          height="1080"
          src="/images/download/toolbar3-1.png"
          class="attachment-full size-full wp-image-386"
          :alt="c.alt.toolbar"
        />
      </picture>
    </div>
    <div class="py-8 flex flex-col gap-8">
      <h2 class="text-white font-russo text-[22px] md:text-[28px]">
        <span v-html="c.howItWorksH2"></span>
      </h2>
      <router-link to="/how-to-create-a-cross-country">
        <h2 class="text-[#FF0000] font-helvetica text-[22px] font-black mb-8">
          {{ c.ccFormat }}
        </h2>
        <picture class="contents">
          <source srcset="/images/download/TSD-CC-q9r65dskcjlulkbvf0smss8qxohyeu0iw4e7rowidu.webp" type="image/webp" />
          <img
          decoding="async"
          width="450"
          height="117"
          src="/images/download/TSD-CC-q9r65dskcjlulkbvf0smss8qxohyeu0iw4e7rowidu.png"
          class="flex m-auto"
          :alt="c.alt.ccFormat"
          />
        </picture>
      </router-link>
      <router-link to="/how-to-create-a-road-rally">
        <h2 class="text-[#FF0000] font-helvetica text-[22px] font-black mb-8">
          {{ c.rrFormat }}
        </h2>
        <picture class="contents">
          <source srcset="/images/download/TSD-RR-q9r65dskcjly2sa01ydcne4ulmu2j190xm4nbam20s.webp" type="image/webp" />
          <img
          decoding="async"
          loading="lazy"
          width="450"
          height="134"
          src="/images/download/TSD-RR-q9r65dskcjly2sa01ydcne4ulmu2j190xm4nbam20s.png"
          class="flex m-auto"
          :alt="c.alt.rrFormat"
          />
        </picture>
      </router-link>
    </div>
    <div class="py-8 flex flex-col gap-4">
      <h2
        v-for="(f, i) in c.featuresA"
        :key="'a' + i"
        class="font-helvetica text-[22px] font-bold text-[#999999]"
        v-html="f"
      ></h2>
      <picture class="contents">
        <source srcset="/images/download/PDF-Print.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="1920"
          height="1080"
          src="/images/download/PDF-Print.png"
          class="attachment-full size-full wp-image-383"
          :alt="c.alt.pdf"
        />
      </picture>
      <h2
        v-for="(f, i) in c.featuresB"
        :key="'b' + i"
        class="font-helvetica text-[22px] font-bold text-[#999999]"
        v-html="f"
      ></h2>
      <picture class="contents">
        <source srcset="/images/download/Cross-Country.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="1920"
          height="1080"
          src="/images/download/Cross-Country.jpg"
          class="attachment-full size-full wp-image-382"
          :alt="c.alt.crossCountry"
        />
      </picture>
      <h2
        v-for="(f, i) in c.featuresC"
        :key="'c' + i"
        class="font-helvetica text-[22px] font-bold text-[#999999]"
        v-html="f"
      ></h2>
    </div>
    <div class="py-8 flex flex-col gap-4">
      <h2 class="text-[#FF0000] text-2xl md:text-3xl font-russo">
        <span v-html="c.ccrrH2"></span>
      </h2>
      <div class="grid grid-cols-2">
        <router-link to="how-to-create-a-cross-country">
          <picture class="contents">
            <source srcset="/images/download/CC-Roadbook-Image-1-qbqkj0exbp9ferwltta62hjf9a8c1uxhvtx4puh3y4.webp" type="image/webp" />
            <img width="450" height="710"
            decoding="async"
            src="/images/download/CC-Roadbook-Image-1-qbqkj0exbp9ferwltta62hjf9a8c1uxhvtx4puh3y4.png"
            :title="c.alt.ccImage"
            :alt="c.alt.ccImage"
            loading="lazy"
            class="flex m-auto"
            />
          </picture>
        </router-link>
        <router-link to="how-to-create-a-road-rally">
          <picture class="contents">
            <source srcset="/images/download/Road-Rally-Roadbook-2-qbqkj0exbp9ferwltta62hjf9a8c1uxhvtx4puh3y4.webp" type="image/webp" />
            <img width="450" height="710"
            decoding="async"
            src="/images/download/Road-Rally-Roadbook-2-qbqkj0exbp9ferwltta62hjf9a8c1uxhvtx4puh3y4.png"
            :title="c.alt.rrImage"
            :alt="c.alt.rrImage"
            loading="lazy"
            class="flex m-auto"
            />
          </picture>
        </router-link>
      </div>
      <h2 class="text-white font-russo text-[22px] md:text-[28px]">
        {{ c.feedbackH2 }}
      </h2>
      <h2
        class="text-[#FF0000] font-helvetica text-[22px] font-black hover:underline"
      >
        <a
          href="mailto:support@rallynavigator.com?subject=Feedback%20from%20Rally%20Navigator%202.0%20Beta%20Test"
        >
          SUPPORT@RALLYNAVIGATOR.COM
        </a>
      </h2>
      <ADVRider />
    </div>
  </section>
  <section class="my-8 mx-auto max-w-[1400px]">
    <FeaturesBanner />
    <PartnersSmall />
    <RallyLogoDownload :to="selfPath" />
  </section>
</template>

<script>
import ADVRider from './ADVRider.vue';
import FeaturesBanner from './FeaturesBanner.vue';
import PartnersSmall from './PartnersSmall.vue';
import PricingTable from './PricingTable.vue';
import RallyLogoDownload from './RallyLogoDownload.vue';
import PhoneDownloadNotice from './PhoneDownloadNotice.vue';
import { downloadClicked } from '../downloads';
import LanguageNav from './LanguageNav.vue';
import { DOWNLOAD_CONTENT, DOWNLOAD_LANGS, downloadPath } from '../content/downloadPage';
// Generated at build time by scripts/fetch-version.js from the download
// server's latest.yml. Do not hand-edit; see that script for why.
import appVersion from '../generated/app-version.json';
// Also generated by scripts/fetch-version.js, from the download server's previous.json.
import previousVersion from '../generated/previous-version.json';

export default {
  name: 'DownloadPage',
  components: {
    LanguageNav,
    PhoneDownloadNotice,
    FeaturesBanner,
    RallyLogoDownload,
    PartnersSmall,
    PricingTable,
    ADVRider,
  },
  // No longer hand-maintained: every build reads the published version from
  // the download server, so the page cannot advertise a version other than the
  // one the buttons hand out. Re-deploy to pick up a new release.
  data() {
    return {
      latestVersion: appVersion.version,
      releaseDate: appVersion.releaseDate,
      previous: previousVersion,
    };
  },
  computed: {
    lang() {
      const l = this.$route.meta && this.$route.meta.lang;
      return DOWNLOAD_CONTENT[l] ? l : 'en';
    },
    c() {
      return DOWNLOAD_CONTENT[this.lang];
    },
    selfPath() {
      return downloadPath(this.lang);
    },
    // Language switcher only on the download pages that exist in several languages.
    isTranslatedFamily() {
      return true;
    },
    languageOptions() {
      const names = { en: 'English', fr: 'Français', es: 'Español', it: 'Italiano', de: 'Deutsch', nl: 'Nederlands' };
      return ['en', ...DOWNLOAD_LANGS].map((code) => ({ code, name: names[code], path: downloadPath(code) }));
    },
    previousPlatforms() {
      return [
        { key: 'windows', label: 'Windows', url: this.previous.windows },
        { key: 'mac', label: 'Mac', url: this.previous.mac },
        { key: 'linux', label: 'Linux', url: this.previous.linux },
      ];
    },
  },
  methods: {
    // Release dates come from the build as English text ("September 28, 2026");
    // show them in the page's language. English keeps the original text.
    localDate(text) {
      if (!text || this.lang === 'en') return text;
      const d = new Date(text);
      if (isNaN(d)) return text;
      return d.toLocaleDateString(this.lang, { day: 'numeric', month: 'long', year: 'numeric' });
    },
    trackDownload(platform, url = null) {
      // GA4 key event download_<platform>[_previous]_clicked + the next-steps pop-up.
      const [os, previous] = platform.split('_');
      downloadClicked(os, { variant: previous ? '_previous' : '', location: 'download_page', url });
    }
  }
};
</script>

<style scoped></style>
